import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

import { faCircleCheck, faCircleXmark, faGear, faPencil } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Icon, Link, Text } from "@performyard/py-components";
import { Avatar } from "@performyard/py-components";

import FormattedDateTime from "../../../../components/FormattedDateTime";
import InputCheckbox from "../../../../components/Inputs/Checkbox";
import Tooltip from "../../../../components/Tooltip";
import FloatingDropdown from "./FloatingDropdown";
import { getMeetingGoalActionOptions } from "./MeetingGoalListItem";

import type { ChecklistItemProps } from "../types";

import { config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Individual checklist item for agenda or action items.
 * Supports editing, status toggle, and assignment (for action items).
 * Memoized to prevent unnecessary re-renders in lists.
 */
const ChecklistItem = memo(
	({
		id,
		name,
		dueDate,
		status,
		creatorId,
		userId,
		isEditing,
		assigneeId,
		assigneeName,
		isNewItem = false,
		meetingId,
		itemType,
		onEdit,
		onNameChange,
		onStatusChange,
		onSave,
		currentEditValue,
		onAssignmentClick,
		goal,
		goalId,
		onGoalActionSelect,
	}: ChecklistItemProps): React.ReactElement => {
		const textareaRef = useRef<HTMLTextAreaElement>(null);
		const { isInteractionDisabled } = useMeetingContext();
		const [isGoalMenuOpen, setIsGoalMenuOpen] = useState(false);

		const isActionItem = itemType === "action";
		const testIdPrefix = isActionItem ? "action-item" : "agenda-item";
		const isGoalItem = Boolean(goalId || goal);

		const isChecked = status === "complete";
		const isCreator = userId === creatorId;
		const isAssignee = Boolean(assigneeId) && userId === assigneeId;
		const isCheckboxDisabled =
			isInteractionDisabled || (!isActionItem && !isCreator) || (isActionItem && !isAssignee && !isCreator);
		const goalActionOptions = useMemo(
			() => (goal ? getMeetingGoalActionOptions(goal, goal.permissions) : []),
			[goal]
		);

		// Focus textarea when entering edit mode
		useEffect(() => {
			if (isEditing && textareaRef.current) {
				textareaRef.current.select();
			}
		}, [isEditing]);

		const handleKeyDown = useCallback(
			(e: React.KeyboardEvent): void => {
				if (e.key === "Enter" && !e.shiftKey) {
					e.preventDefault();
					if (currentEditValue.trim()) {
						onSave(true);
					}
				} else if (e.key === "Escape") {
					onSave(false);
				}
			},
			[currentEditValue, onSave]
		);

		const handleNameChange = useCallback(
			(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
				onNameChange(e.target.value);
			},
			[onNameChange]
		);

		const handleStatusChange = useCallback(
			(checked: boolean): void => {
				if (isInteractionDisabled) return;
				onStatusChange(checked ? "complete" : "incomplete");
			},
			[isInteractionDisabled, onStatusChange]
		);

		const handleAssignmentClick = useCallback((): void => {
			if (isInteractionDisabled) return;

			if (!isNewItem && meetingId && onAssignmentClick) {
				onAssignmentClick({
					itemId: id,
					meetingId,
					currentAssigneeId: assigneeId,
					dueDate,
					name,
				});
			}
		}, [isInteractionDisabled, isNewItem, meetingId, onAssignmentClick, id, assigneeId, dueDate, name]);

		const renderDueDate = (): React.ReactElement | null => {
			if (!dueDate) return null;

			return (
				<Text size="extra-small" weight="regular">
					Due Date:{" "}
					<FormattedDateTime
						date={dueDate}
						delimiter={"•" as const}
						hasTime={true}
						dateSize="medium"
						size="extra-small"
						weight="regular"
					/>
				</Text>
			);
		};

		if (isEditing) {
			return (
				<div className="flex items-center gap-2">
					<div className="flex flex-1 items-center justify-between rounded-sm p-2 outline outline-1 outline-neutral-3">
						<div className="flex w-full items-center space-x-2">
							<InputCheckbox value={isChecked} disabled={true} />
							<div className="flex w-full flex-col space-y-[7px]">
								<ReactTextareaAutosize
									ref={textareaRef}
									id={`${id}-input`}
									data-testid={`${testIdPrefix}-${id}-input`}
									className="w-full resize-none border-0 bg-transparent p-0 text-paragraph-3"
									value={currentEditValue}
									onChange={handleNameChange}
									placeholder={`Your ${itemType} item goes here`}
									minRows={config.layout.checklistInputMinRows}
									onKeyDown={handleKeyDown}
									autoFocus
								/>
								{renderDueDate()}
							</div>
						</div>
						<div className="flex items-center">
							<button
								data-testid={`${testIdPrefix}-${id}-confirm-button`}
								className="flex items-center justify-center border-0 bg-transparent"
								style={{ width: config.layout.buttonSize, height: config.layout.buttonSize }}
								disabled={!currentEditValue.trim()}
								onClick={() => onSave(true)}
							>
								<Icon
									icon={faCircleCheck}
									size={config.iconSize.medium}
									color="green-3"
									disabled={!currentEditValue.trim()}
									dangerouslySetClassName={
										currentEditValue.trim()
											? "hover:cursor-pointer hover:text-green-5 active:text-green-7"
											: "cursor-not-allowed"
									}
								/>
							</button>
							<button
								data-testid={`${testIdPrefix}-${id}-cancel-button`}
								className="flex items-center justify-center border-0 bg-transparent"
								style={{ width: config.layout.buttonSize, height: config.layout.buttonSize }}
								onClick={() => onSave(false)}
							>
								<Icon
									icon={faCircleXmark}
									size={config.iconSize.medium}
									color="red-3"
									dangerouslySetClassName="hover:cursor-pointer hover:text-red-5 active:text-red-7"
								/>
							</button>
						</div>
					</div>
					{isActionItem && <div className="w-6" />}
				</div>
			);
		}

		return (
			<div className="flex items-center gap-2">
				<div className="flex flex-1 items-center justify-between rounded-sm bg-neutral-1 p-2 outline outline-1 outline-neutral-3">
					<div className="flex items-center space-x-2">
						<InputCheckbox
							value={isChecked}
							onChange={handleStatusChange}
							disabled={isCheckboxDisabled}
							data-testid={`${testIdPrefix}-${id}-checkbox`}
						/>
						<div className="flex w-full flex-col space-y-2">
							{isGoalItem ? (
								<div className="flex flex-wrap items-center gap-1">
									<Text size="paragraph-3" weight="regular">
										Goal:
									</Text>
									<Link
										href={`/goal/${goal?._id || goalId}/`}
										forceHardReload={true}
										dangerouslySetClassName="no-underline hover:underline"
									>
										<Text size="paragraph-3" weight="regular" color="primary-5">
											{goal?.name || name}
										</Text>
									</Link>
								</div>
							) : (
								<Text size="paragraph-3" weight="regular">
									{name}
								</Text>
							)}
							{renderDueDate()}
						</div>
					</div>
					<div className="flex items-center">
						{!isInteractionDisabled && (
							<>
								{isGoalItem ? (
									goal && onGoalActionSelect && goalActionOptions.length > 0 ? (
										<FloatingDropdown
											isOpen={isGoalMenuOpen}
											onOpenChange={setIsGoalMenuOpen}
											onSelect={(option) => onGoalActionSelect(option.id, goal)}
											trigger={
												<button
													data-testid={`${testIdPrefix}-${id}-goal-actions-button`}
													className="flex items-center justify-center border-0 bg-transparent"
													style={{
														width: config.layout.buttonSize,
														height: config.layout.buttonSize,
													}}
													disabled={isInteractionDisabled}
													aria-label="Goal actions menu"
												>
													<Icon
														icon={faGear}
														size={config.iconSize.medium}
														color="neutral-3"
														disabled={isInteractionDisabled}
														dangerouslySetClassName={
															!isInteractionDisabled
																? "hover:cursor-pointer hover:text-neutral-5 active:text-neutral-7"
																: "cursor-not-allowed"
														}
													/>
												</button>
											}
											options={goalActionOptions.map((option) => ({
												id: option.id,
												label: (
													<Text size="caption" weight="regular" color="neutral-5">
														{option.label}
													</Text>
												),
											}))}
										/>
									) : goalId && !goal ? (
										<button
											className="flex items-center justify-center border-0 bg-transparent"
											style={{
												width: config.layout.buttonSize,
												height: config.layout.buttonSize,
											}}
											disabled={true}
											aria-label="Loading goal actions"
										>
											<Icon
												icon={faGear}
												size={config.iconSize.medium}
												color="neutral-2"
												disabled={true}
												dangerouslySetClassName="cursor-wait"
											/>
										</button>
									) : null
								) : (
									<button
										data-testid={`${testIdPrefix}-${id}-edit-button`}
										className="flex items-center justify-center border-0 bg-transparent"
										style={{ width: config.layout.buttonSize, height: config.layout.buttonSize }}
										onClick={
											isCreator && !isInteractionDisabled ? () => onEdit(id, name) : undefined
										}
									>
										<Icon
											icon={faPencil}
											size={config.iconSize.medium}
											color="neutral-3"
											disabled={!isCreator || isInteractionDisabled}
											dangerouslySetClassName={
												isCreator && !isInteractionDisabled
													? "hover:cursor-pointer hover:text-neutral-5 active:text-neutral-7"
													: "cursor-not-allowed"
											}
										/>
									</button>
								)}
								<button
									data-testid={`${testIdPrefix}-${id}-delete-button`}
									className="flex items-center justify-center border-0 bg-transparent"
									style={{ width: config.layout.buttonSize, height: config.layout.buttonSize }}
									onClick={
										isCreator && !isInteractionDisabled
											? () => onStatusChange("deleted")
											: undefined
									}
								>
									<Icon
										icon={faCircleXmark}
										size={config.iconSize.medium}
										color="neutral-3"
										disabled={!isCreator || isInteractionDisabled}
										dangerouslySetClassName={
											isCreator && !isInteractionDisabled
												? "hover:cursor-pointer hover:text-neutral-5 active:text-neutral-7"
												: "cursor-not-allowed"
										}
									/>
								</button>
							</>
						)}
					</div>
				</div>
				{isActionItem && (
					<div
						className={
							!isNewItem && isCreator && !isInteractionDisabled
								? "hover:cursor-pointer"
								: "cursor-not-allowed opacity-50"
						}
					>
						<Tooltip message={!assigneeId ? "Unassigned" : assigneeName || ""} placement="top">
							<div onClick={isCreator && !isInteractionDisabled ? handleAssignmentClick : undefined}>
								<Avatar
									id={assigneeId}
									size={config.layout.smallAvatarSize}
									testId={`${testIdPrefix}-${id}-avatar`}
								/>
							</div>
						</Tooltip>
					</div>
				)}
			</div>
		);
	}
);

ChecklistItem.displayName = "ChecklistItem";

export default ChecklistItem;
